import * as React from 'react';
import { useQuery, Direction } from './Query';
import { FilterHeader } from './QueryTable';
import Modal, { ModalSize } from '../misc/components/modal';
import Select from 'react-select';

export interface Option {
    label: any;
    value: any
}

interface MatchSortCollectionHeaderProps {
    title?: string;
    orderField: string;
    options: Option[];
    sortField?: string;
    primarySubClauseField: string;
    automationId?: string;
    sortEnabled?: boolean;
}

export const MatchSortCollectionHeader = ({ title, orderField, primarySubClauseField, options, sortField = null, automationId = null, sortEnabled = true }: MatchSortCollectionHeaderProps) => {
    const [display, setDisplay] = React.useState(false);
    const { filters, ordering, applyOrdering } = useQuery();
    const [ordered, setOrdered] = React.useState<Direction>();
    const [filtered, setFiltered] = React.useState(false);
    sortField = sortField ?? orderField;

    React.useEffect(() => {
        const filtered = filters.some(f => (f.field === sortField && f.condition === 'any'));
        setFiltered(filtered);
    }, [filters]);

    React.useEffect(() => {

        const ordered = ordering.find(o => o.field === sortField);
        setOrdered(ordered
            ? ordered.condition
            : null);
    }, [ordering]);

    const handleSort = () => {
        let direction: Direction = 'asc';

        if (ordered === 'asc')
            direction = 'desc';

        applyOrdering([{ field: sortField, condition: direction }]);
    }

    const handleFilter = () => {
        setDisplay(true);
    }

    const modal = display
        ? (
            <MatchSortCollectionFilterModal
                title={title}
                field={sortField}
                primarySubClauseField={primarySubClauseField}
                options={options}
                onClose={() => setDisplay(false)} />
        )
        : null;

    return (
        <>
            <FilterHeader automationId={automationId} filtered={filtered} sorted={ordered} onFilter={handleFilter} onSort={handleSort} sortEnabled={sortEnabled}>{title}</FilterHeader>
            {modal}
        </>
    );
}

interface MatchSortCollectionFilterModalProps {
    title: string;
    options: any[];
    field: string;
    primarySubClauseField: string;
    onClose: () => void;
}

export const MatchSortCollectionFilterModal = ({ title, options, field, primarySubClauseField, onClose }: MatchSortCollectionFilterModalProps) => {
    const [selectedValues, setSelectedValues] = React.useState<any[]>([]);

    const { filters, applyFilters, paging, applyPaging } = useQuery();

    React.useEffect(() => {
        const currentSelectedValues = filters
            .filter(f => f.field === field && f.condition === 'any' && f.primarySubClauseField === primarySubClauseField)
            .flatMap(f => f.values);

        setSelectedValues(currentSelectedValues);
    }, [filters]);

    const handleApply = () => {
        let newFilters = filters.filter(f => (f.field !== field && f.primarySubClauseField !== primarySubClauseField));

        selectedValues && selectedValues.length > 0 && (
            newFilters = [...newFilters,
            {
                field: field,
                condition: 'any',
                primarySubClauseField: primarySubClauseField,
                primarySubQueryCondition: 'eq',
                values: selectedValues, 
            }
            ]);

        applyPaging({ ...paging, page: 1 });
        applyFilters(newFilters);
        onClose();
    }

    const handleClear = () => {
        applyPaging({ ...paging, page: 1 });

        applyFilters(filters.filter(
            f =>
            (f.field !== field)
        ));

        onClose();
    }

    return (
        <Modal header={`${title} Filter`} size={ModalSize.Normal} show={true} closeModalHandler={onClose}>
            <div className="modal-body">
                <label>{title}</label>
                <Select
                    id="match-sort-collection-match"
                    name="match1"
                    isMulti={true}
                    classNamePrefix="multiSelect"
                    options={options}
                    autoFocus={true} 
                    value={selectedValues.map(v => options.find(o => o.value === v))}
                    getOptionLabel={o => o.label}
                    getOptionValue={o => o.value}
                    onChange={(o: any[]) => setSelectedValues(o.map(v => v.value))}
                />
            </div>
            <div className="modal-footer">
                <button className="btn btn-primary" onClick={handleApply} data-automationid="submit-filter">OK</button>
                <button className="btn btn-danger" onClick={handleClear} data-automationid="clear-filter">Clear</button>
            </div>
        </Modal>
    );
}
